/**
 * Modale du choix d'échelle, quand un modèle réglementaire (.lfgr) est appliqué
 * sur une étiquette d'un AUTRE format que celle qui l'a produit.
 *
 * Pourquoi une question et pas une règle : les positions d'un modèle sont des
 * millimètres absolus. Sur une étiquette plus petite, les appliquer tels quels
 * pousse les mentions obligatoires hors du plan de travail — visibles à l'écran,
 * coupées à l'export. Mais tout réduire n'est pas non plus automatiquement bon :
 * **le corps d'une mention réglementaire a des minimums légaux**, et la réduire
 * peut la rendre non conforme. Le logiciel ne peut pas trancher ça seul.
 *
 * D'où trois portes, et une seule question réellement posée : adapte-t-on aussi
 * la taille du texte ?
 */
import React from 'react';

const Carte = ({ titre, detail, recommande, onClick }) => (
    <button
        type="button"
        onClick={onClick}
        className={`w-full text-left rounded-xl border p-4 transition-all group ${
            recommande
                ? 'border-forge-accent/50 bg-forge-accent/10 hover:bg-forge-accent/15'
                : 'border-forge-border/20 bg-black/20 hover:bg-white/5 hover:border-forge-border/40'
        }`}
    >
        <div className="flex items-center gap-2 mb-1">
            <span className={`text-[11px] font-bold tracking-wide ${recommande ? 'text-forge-accent' : 'text-forge-text'}`}>
                {titre}
            </span>
            {recommande && (
                <span className="text-[8px] font-mono font-bold uppercase tracking-[0.15em] text-forge-accent/80 border border-forge-accent/30 rounded px-1.5 py-0.5">
                    conseillé
                </span>
            )}
        </div>
        <p className="text-[10px] leading-relaxed text-forge-muted/80">{detail}</p>
    </button>
);

/**
 * @param {object}   props
 * @param {object|null} props.demande - { nom, formatModele: {width_mm,height_mm}, formatCible: {…} }
 * @param {(choix: 'tout'|'positions'|'tel-quel') => void} props.onChoisir
 * @param {() => void} props.onAnnuler
 */
export default function RegulatoryTemplateScaleModal({ demande, onChoisir, onAnnuler }) {
    if (!demande) return null;
    const { nom, formatModele, formatCible } = demande;
    const fmt = (f) => `${Math.round(f.width_mm * 10) / 10} × ${Math.round(f.height_mm * 10) / 10} mm`;
    const retrecit = (formatCible.width_mm * formatCible.height_mm)
                   < (formatModele.width_mm * formatModele.height_mm);

    return (
        <div className="fixed inset-0 z-[150] flex items-center justify-center modal-backdrop backdrop-blur-md p-6 font-mono">
            <div className="bg-forge-bg/95 backdrop-blur-md border border-forge-border/25 w-full max-w-[520px] rounded-2xl shadow-[0_25px_80px_rgba(0,0,0,0.5)] overflow-hidden">

                <div className="px-6 py-4 border-b border-forge-border/20 flex items-center gap-3">
                    <div className="w-8 h-8 rounded-lg bg-forge-lemon/15 border border-forge-lemon/25 flex items-center justify-center shrink-0">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" className="text-forge-lemon">
                            <path d="M15 3h6v6" /><path d="M9 21H3v-6" /><path d="M21 3l-7 7" /><path d="M3 21l7-7" />
                        </svg>
                    </div>
                    <div>
                        <h2 className="text-[12px] font-bold text-forge-text uppercase tracking-[0.2em]">Format différent</h2>
                        <p className="text-[9px] text-forge-muted/50 tracking-widest uppercase">Modèle réglementaire</p>
                    </div>
                </div>

                <div className="px-6 py-5 space-y-4">
                    <p className="text-[11px] leading-relaxed text-forge-text/80 font-sans">
                        {nom ? <>Le modèle <strong className="text-forge-text">« {nom} »</strong> a été conçu pour</> : <>Ce modèle a été conçu pour</>}
                        {' '}une étiquette de <strong className="text-forge-text">{fmt(formatModele)}</strong>.
                        La vôtre fait <strong className="text-forge-text">{fmt(formatCible)}</strong>.
                    </p>

                    <div className="space-y-2">
                        <Carte
                            titre="Adapter les positions et la taille du texte"
                            detail={retrecit
                                ? "Tout suit le nouveau format. Vérifiez ensuite que les mentions obligatoires restent lisibles : leur corps a des minimums légaux."
                                : "Tout suit le nouveau format, texte compris."}
                            recommande
                            onClick={() => onChoisir('tout')}
                        />
                        <Carte
                            titre="Adapter les positions seulement"
                            detail="Les calques se replacent sur la nouvelle étiquette, mais les corps de texte ne changent pas. À choisir quand les mentions sont déjà à leur taille minimale."
                            onClick={() => onChoisir('positions')}
                        />
                        <Carte
                            titre="Appliquer tel quel"
                            detail="Les millimètres du modèle sont repris à l'identique. Ce qui tombe hors du plan de travail sera coupé à l'export."
                            onClick={() => onChoisir('tel-quel')}
                        />
                    </div>
                </div>

                <div className="px-6 py-3 border-t border-forge-border/20 flex justify-end">
                    <button
                        onClick={onAnnuler}
                        className="text-[10px] font-bold uppercase tracking-[0.15em] text-forge-muted hover:text-forge-text px-3 py-2 rounded-lg hover:bg-white/5 transition-colors"
                    >
                        Annuler
                    </button>
                </div>
            </div>
        </div>
    );
}
